import moment from "moment";

import localizations from "@src/localizations";
import { useStores } from "@stores";
import { AreaUnit, LengthUnit, WeightUnit } from "@services/demosService";
import {
  convertYield,
  convertWeight,
  convertArea,
  convertLength,
  convertSpeed,
  speedUnitFor,
  SpeedUnit,
} from "@src/utils/convert";

export const useLang = () => {
  const { settingsStore } = useStores();

  const code = settingsStore.language.toLowerCase();
  if (moment.locale() !== code) {
    moment.locale(code);
  }

  return localizations[settingsStore.language];
};

export const useLangCode = () => {
  const { settingsStore } = useStores();
  return settingsStore.language.toLowerCase();
};

export const useMoment = () => {
  const { settingsStore } = useStores();
  return (
    inp?: moment.MomentInput,
    format?: moment.MomentFormatSpecification,
    strict?: boolean
  ) => {
    return moment(inp, format, strict).locale(settingsStore.language.toLowerCase());
  }
};

export const useSettings = () => {
  const { settingsStore } = useStores();
  return settingsStore.units;
};

export const useConvert = () => {
  const { settingsStore } = useStores();
  const units = settingsStore.units;
  return {
    convertWeight: (
      value: number,
      from: WeightUnit = WeightUnit.Centner,
      to: WeightUnit = units.weight,
    ): number => convertWeight(value, from, to, {
      poundsPerBushel: units.poundsPerBushel,
      poundsPerBag: units.poundsPerBag,
    }),
    convertArea: (
      value: number,
      from: AreaUnit | string = AreaUnit.Hectare,
      to: AreaUnit = units.area,
    ): number => convertArea(value, from, to),
    convertLength: (
      value: number,
      from: LengthUnit | string = LengthUnit.Meter,
      to: LengthUnit = units.length,
    ): number => convertLength(value, from, to),
    convertYield: (
      value: number,
      toWeight: WeightUnit = units.weight,
      toArea: AreaUnit = units.area,
    ): number => convertYield(value, toWeight, toArea, {
      poundsPerBushel: units.poundsPerBushel,
      poundsPerBag: units.poundsPerBag,
    }),
  };
};

export const useWeight = () => {
  const lang = useLang();
  const { settingsStore } = useStores();
  const units = settingsStore.units;
  return (unit: WeightUnit = units.weight) => {
    switch (unit) {
      case WeightUnit.Centner:
        return lang.unit.centner;
      case WeightUnit.Pound:
        return lang.unit.pound;
      case WeightUnit.ShortTon:
        return lang.unit.shortTon;
      case WeightUnit.Kilogram:
        return lang.unit.kilogram;
      case WeightUnit.Bushel:
        return lang.unit.bushel;
      case WeightUnit.Ton:
        return lang.unit.ton;
      case WeightUnit.Bag:
        return lang.unit.bag;
      default:
        return String(unit);
    }
  };
}

export const useArea = () => {
  const lang = useLang();
  const { settingsStore } = useStores();
  const units = settingsStore.units;
  return (unit: AreaUnit = units.area) => {
    switch (unit) {
      case AreaUnit.Hectare:
        return lang.unit.ha;
      case AreaUnit.Acre:
        return lang.unit.acre;
      default:
        return String(unit);
    }
  };
}

/**
 * Format an area given in HECTARES in the user's area unit, e.g. "12.40 ac".
 * The one area formatter: the field list, edit card, stats panels, trials and
 * correction zones each formatted it their own way (and the edit card always
 * said "ha"). Returns "" for a missing or non-finite area.
 */
export const useFormatArea = () => {
  const { convertArea } = useConvert();
  const locArea = useArea();
  return (hectares: number | null | undefined, digits = 2): string =>
    hectares != null && Number.isFinite(hectares)
      ? `${convertArea(hectares, AreaUnit.Hectare).toFixed(digits)} ${locArea()}`
      : "";
};

export const useLength = () => {
  const lang = useLang();
  const { settingsStore } = useStores();
  const units = settingsStore.units;
  return (unit: LengthUnit = units.length) => {
    switch (unit) {
      case LengthUnit.Meter:
        return lang.unit.meter;
      case LengthUnit.Foot:
        return lang.unit.foot;
      default:
        return String(unit);
    }
  };
}

/**
 * The user's speed unit (mph for acre users, km/h otherwise) with converters
 * to and from the km/h every API speaks.
 *
 * Not memoised — like useConvert(), it is a fresh object every render, so never
 * put it (or one of its functions) in an effect dependency array.
 */
export const useSpeed = () => {
  const lang = useLang();
  const { settingsStore } = useStores();
  const unit = speedUnitFor(settingsStore.units.length);
  return {
    unit,
    label: unit === SpeedUnit.MilePerHour ? lang.unit.mph ?? "mph" : lang.unit.kmh ?? "km/h",
    fromKmh: (kmh: number) => convertSpeed(kmh, SpeedUnit.KmPerHour, unit),
    toKmh: (value: number) => convertSpeed(value, unit, SpeedUnit.KmPerHour),
  };
};

export const useYield = () => {
  const { settingsStore } = useStores();
  const locArea = useArea();
  const locWeight = useWeight();
  const units = settingsStore.units;

  return (
    weight: WeightUnit = units.weight,
    area: AreaUnit = units.area,
  ) => `${locWeight(weight)}/${locArea(area)}`;
};
